import { convertAudioToWav } from "../utils/audio";

export interface RecordingResult {
  audioBlob: Blob | null;
  duration: number;
}

class AudioRecordingService {
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private audioChunks: Blob[] = [];
  private startTime: number = 0;
  private isRecording: boolean = false;

  /**
   * Start recording audio from the microphone
   * @param options Optional recording options
   * @returns Promise that resolves when recording has started
   */
  async startRecording(options?: {
    mimeType?: string;
    audioBitsPerSecond?: number;
  }): Promise<void> {
    if (this.isRecording) {
      throw new Error("Recording is already in progress");
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaStream = stream;
      this.audioChunks = [];
      this.startTime = Date.now();

      const mimeType = options?.mimeType || "audio/webm;codecs=opus";

      // Check if the mimeType is supported
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        console.warn(
          `MIME type ${mimeType} not supported, falling back to default`
        );
      }

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported(mimeType)
          ? mimeType
          : undefined,
        audioBitsPerSecond: options?.audioBitsPerSecond,
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      console.log("Audio recording gestart");
    } catch (error) {
      console.error("Fout bij starten audio recording:", error);
      this.cleanup();
      throw new Error(
        `Kon audio recording niet starten: ${(error as Error).message}`
      );
    }
  }

  /**
   * Stop recording and return the recorded audio
   * @param convertToWav If true, converts the audio to WAV format (default: true)
   * @returns Promise that resolves with the recording result
   */
  async stopRecording(convertToWav: boolean = true): Promise<RecordingResult> {
    if (!this.isRecording || !this.mediaRecorder) {
      throw new Error("No recording in progress");
    }

    return new Promise<RecordingResult>((resolve) => {
      const duration = Date.now() - this.startTime;

      const processAudio = (rawBlob: Blob | null): void => {
        // Process audio conversion asynchronously
        const processConversion = async () => {
          let audioBlob: Blob | null = rawBlob;

          // Convert to WAV if requested and blob exists
          if (convertToWav && audioBlob) {
            try {
              audioBlob = await convertAudioToWav(audioBlob);
            } catch (error) {
              console.warn(
                "Kon audio niet converteren naar WAV, gebruikt origineel:",
                error
              );
              // Continue with original blob if conversion fails
            }
          }

          this.cleanup();

          resolve({
            audioBlob,
            duration,
          });
        };

        void processConversion();
      };

      this.mediaRecorder!.onstop = () => {
        let rawBlob: Blob | null = null;

        if (this.audioChunks.length > 0) {
          const mimeType =
            this.mediaRecorder?.mimeType || "audio/webm;codecs=opus";
          rawBlob = new Blob(this.audioChunks, { type: mimeType });
        }

        processAudio(rawBlob);
      };

      if (this.mediaRecorder?.state === "recording") {
        this.mediaRecorder.stop();
      } else {
        // Already stopped, process immediately
        const mimeType =
          this.mediaRecorder?.mimeType || "audio/webm;codecs=opus";
        const rawBlob =
          this.audioChunks.length > 0
            ? new Blob(this.audioChunks, { type: mimeType })
            : null;
        processAudio(rawBlob);
      }
    });
  }

  /**
   * Check if currently recording
   */
  getRecordingState(): boolean {
    return this.isRecording;
  }

  /**
   * Cancel the current recording without returning the audio
   */
  cancelRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    this.cleanup();
  }

  /**
   * Clean up resources
   */
  private cleanup(): void {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isRecording = false;
    this.startTime = 0;
  }

  /**
   * Clean up all resources (public method for cleanup)
   */
  dispose(): void {
    this.cancelRecording();
  }
}

export default AudioRecordingService;
